import { PLAN_OTHER } from '@/utils/studentPlan.js'

// Client-side source of truth for who may manage (create/edit/delete) a
// student's meetings, and who sees meeting info at all. Mirrors
// canEditMeetingFor() / canReadMeetingFor() in firestore.rules — the server is
// the real boundary; these helpers keep the UI from offering actions that
// would be denied. A third mirror lives in functions/calendarSync/reconcile.js
// (assertCanEditMeetingsFor) gating the reconcileMeetings callable.
// KEEP THE THREE IN SYNC.

// Roles that qualify as a student's case manager of record when listed in
// caseManagerId — every role assignable in the CM dropdown (sped chairs and
// the 504 coordinator carry caseloads). Plain admins never qualify even if
// listed. admin_504 additionally gets ALL 504 students via canManageMeetings.
const CASE_MANAGER_MEETING_ROLES = [
  'case_manager',
  'case_manager_full',
  'speech_case_manager',
  'sped_chair',
  'sped_chair_all',
  'admin_504'
]

export function getStudentCaseManagerId(student) {
  return (
    student?.app?.studentData?.caseManagerId ||
    student?.caseManagerId ||
    student?.casemanager_id ||
    null
  )
}

/** Cloud-Function mirror of the open eval's owner. Empty when none is open. */
export function getStudentEvalCaseManagerId(student) {
  const raw = student?.app?.evalCaseManagerId || student?.evalCaseManagerId || ''
  return String(raw).trim()
}

/** Plan CM of record, or the open-eval CM. Meetings / staffIds — not the CM pill. */
export function isPlanOrEvalCaseManager(student, uid) {
  if (!uid || !student) return false
  return getStudentCaseManagerId(student) === uid || getStudentEvalCaseManagerId(student) === uid
}

export function getStudentPlan(student) {
  return student?.app?.studentData?.plan || student?.plan || ''
}

/** School-wide meeting coordinators stored on meetingDefaults/{schoolId}. */
export function isSchoolMeetingCoordinator(user, coordinatorUids = []) {
  const uid = user?.uid
  if (!uid || user?.role === 'paraeducator') return false
  return Array.isArray(coordinatorUids) && coordinatorUids.includes(uid)
}

// Meeting writes belong to the student's case manager of record — not admins
// or staff_edit (those keep read access), and not sped chairs school-wide
// (only for students where they ARE the CM of record). The 504 coordinator
// acts as the case manager for 504 students, so admin_504 keeps write access
// for 504 plans even when the doc lists another caseManagerId.
// School meeting coordinators (meetingDefaults.coordinatorUids) may also
// manage meetings in the app; Google Calendar still treats the CM as organizer.
// Pass coordinatorUids only at meeting UI call sites — never for feedback.
export function canManageMeetings(user, student, { coordinatorUids } = {}) {
  if (!user?.role || !student) return false
  // Other plans are accommodation notes for staff, not a meeting cycle.
  if (getStudentPlan(student) === PLAN_OTHER) return false
  if (CASE_MANAGER_MEETING_ROLES.includes(user.role)) {
    if (getStudentCaseManagerId(student) === user.uid) return true
    if (getStudentEvalCaseManagerId(student) === user.uid) return true
  }
  if (user.role === 'admin_504' && getStudentPlan(student) === '504') return true
  return isSchoolMeetingCoordinator(user, coordinatorUids)
}

/**
 * Who appears on the meeting calendar source list.
 * IEP and 504 students, plus anyone whose open eval is assigned to `uid`.
 * Plain CM roles then narrow to own caseload OR own eval assignment.
 * 504 annual reviews are a separate tile type from IEP plan review.
 */
export function studentOnMeetingCalendar(student, { uid, multiView } = {}) {
  if (!student) return false
  if (getStudentPlan(student) === PLAN_OTHER) return false
  const evalCm = getStudentEvalCaseManagerId(student)
  const plan = getStudentPlan(student)
  const onPlanCalendar = plan === 'IEP' || plan === '504'
  const hasOpenEval = Boolean(evalCm)
  if (multiView) return onPlanCalendar || hasOpenEval
  if (!uid) return false
  if (onPlanCalendar && getStudentCaseManagerId(student) === uid) return true
  return evalCm === uid
}

// Meeting dates stay visible to staff who work with the student (view-only).
// Aides get no meeting surface at all: seeing a student's meetings reveals
// sped status they have no need-to-know for.
export function canSeeMeetingInfo(user) {
  return !!user?.role && user.role !== 'paraeducator'
}

// Who may log a meeting that already happened (gen-ed hours only, no calendar
// invite). Case managers and speech case managers are limited to their own
// caseload. Admins, SPED chairs, the 504 coordinator, and a full case manager
// may log one for any student they can see. Teachers, paras, and other staff
// cannot. KEEP IN SYNC with canRecordHeldMeetingFor() in firestore.rules.
const HELD_MEETING_OVERSIGHT_ROLES = [
  'admin',
  'school_admin',
  'district_admin',
  'sped_chair',
  'sped_chair_all',
  'admin_504',
  'case_manager_full'
]

export function canRecordHeldMeetings(user) {
  if (!user?.role) return false
  return (
    HELD_MEETING_OVERSIGHT_ROLES.includes(user.role) ||
    user.role === 'case_manager' ||
    user.role === 'speech_case_manager'
  )
}

export function canRecordHeldMeetingFor(user, student) {
  if (!canRecordHeldMeetings(user) || !student) return false
  if (HELD_MEETING_OVERSIGHT_ROLES.includes(user.role)) return true
  return canManageMeetings(user, student)
}

// Roles that may OPEN the meetings dialog for a student they can see — the
// case-manager-assignable roles. The dialog renders read-only unless
// canManageMeetings() also passes for that student, so viewing meeting
// details never implies the ability to change them.
export function canViewMeetingsDialog(user, { coordinatorUids } = {}) {
  if (!user?.role) return false
  if (CASE_MANAGER_MEETING_ROLES.includes(user.role)) return true
  return isSchoolMeetingCoordinator(user, coordinatorUids)
}
